import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  onMounted,
  useContext,
  useRoute,
  useStore,
  useAsync,
} from '@nuxtjs/composition-api';
import dayjs from 'dayjs';
import { defineAsyncComponent } from 'vue';
import MegaMenu from './MegaMenu.vue';
import SmallMegaMenu from '~/components/header/small/SmallMegaMenu.vue';
import TsTesLogo from '~/components/svg/TsTesLogo.vue';
import TLinkButton from '~/components/atoms/t-link-button.vue';
import { State } from '~/store';
import { navigationLinks, ResourceNavigationInfo } from '~/data/navigation_links_main';
import { usePlusDownloads } from '~/composables/usePlusDownloads';
import { useUser } from '~/composables/useUser';
import ProfileDropdown from '~/components/header/ProfileDropdown.vue';
import DownloadsPill from '~/components/atoms/DownloadsPill.vue';
import SearchBar from '~/components/search/SearchBar.vue';
import AlgoliaSearchBar from '~/components/search/AlgoliaSearchBar.vue';
import DropdownMenu from '~/components/DropdownMenu.vue';
import TNavigationIconItem from '~/components/header/NavigationDropdownItemWithIcon.vue';
import { useTwoRowHeader } from '~/composables/useTwoRowHeader';
import TButton from '~/components/atoms/t-button.vue';
import { useLocale } from '~/composables/useLocale';


export default /*#__PURE__*/_defineComponent({
  __name: 'TwoRowHeader',
  setup(__props) {

const HeaderBanner = defineAsyncComponent({
  loader: () => import('~/components/HeaderBanner.vue'),
});
const Toast = defineAsyncComponent({ loader: () => import('~/components/Toast.vue') });
const MobileNav = defineAsyncComponent({ loader: () => import('~/components/MobileNav.vue') });

const {
  $config,
  i18n,
  $isFlagOn,
  $addProfitwellScript,
  $cookies,
  $intercom,
  localePath,
  $getExperimentVariant,
  $mzAnalyticsApi,
} = useContext();
const $route = useRoute();
const store = useStore<State>();
const { user } = useUser();
const { doesUserGetPlusDownloads, plusDownloadsLeft } = usePlusDownloads();
const { openNavEvent, linkClickedEvent } = useTwoRowHeader();

useAsync(async () => {
  await store.dispatch('loadTrendingTopics', { country: i18n.locale });
}, `header-${i18n.locale}`);

const isFreeUser = computed(() => user.value?.subscription?.code === 'free');
const isNonPlusMember = computed(() => user.value?.subscription?.code !== 'teach_plus');
const isPlusLiteMember = computed(() => user.value?.subscription?.code === 'plus_lite');
const isSchoolMember = computed(
  () =>
    user.value?.subscription?.code === 'school_ultimate' ||
    user.value?.subscription?.code === 'school_plan' ||
    user.value?.subscription?.code === 'school_plan_member' ||
    user.value?.subscription?.code === 'school_plan_admin_only'
);

const showHeaderBanner = computed(() => store.getters.headerBanner);
const trendingTopics = computed(() => store.getters.trendingTopics);
const showMobileNav = computed(() => store.getters.showMobileNav);
const locale = useLocale();
const location = computed(() => store.state.location);
const u = computed(() => user.value);

const contentDropdown = computed(() => {
  return {
    label: 'Content',
    description: 'Blog, podcast, and webinars',
    items: navigationLinks[locale.value].content_pages,
  };
});

// Not including topics because it has a different structure
const secondRowDropdowns = computed(() => [
  {
    label: 'Resources',
    link: localePath('/teaching-resources/'),
    items: navigationLinks[locale.value].resources,
  },
  {
    label: i18n.tc('learning_areas_label'),
    link: localePath('/learning-areas/'),
    items: navigationLinks[locale.value].learning_areas,
  },
  {
    label: i18n.tc('grade_levels_label'),
    link: localePath('/year-levels/'),
    items: navigationLinks[locale.value].year_levels,
  },
]);

const isResourceDropdown = (item: any): item is ResourceNavigationInfo<'us' | 'au' | 'gb'> => {
  return item.icon !== undefined && item.description !== undefined && item.type !== undefined;
};

onMounted(async () => {
  $addProfitwellScript();
  // Check if location cookie is set. If not, do a geoIP lookup to set it
  let location = $cookies.get($config.cookieLocation);
  if (!location) {
    location = await store.dispatch('getGeoLocation');
  }

  if (user.value) {
    // Expose variables to Google Tag Manager for use across any tags. Initally for Intercom
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push('user_hash', user.value.intercom?.hash);
    (window as any).dataLayer.push('user_id', user.value.id);
    (window as any).dataLayer.push('user_uuid', user.value.uuid);
    (window as any).dataLayer.push({
      event: 'Geo Location',
      user_country: user.value.location.country,
    });

    const isClientSide = typeof (window as any).clarity !== 'undefined';
    if (isClientSide) {
      (window as any).clarity('set', 'UUID', user.value.uuid);
      (window as any).clarity('identify', user.value.uuid);
    }
    checkSubscriptionStatus();
    if (user.value.capabilities.includes('access_dashboard')) {
      // Banner with link that sets the WP auth cookie then sends them to the WP dashboard
      store.dispatch('setHeaderBanner', {
        content: 'Access WordPress dashboard →',
        icon: 'lock',
        link: $config.wpURL + '/?redirect_to=' + encodeURIComponent(`${$config.wpURL}/wp-admin`),
        dismiss: true,
      });
    }
  } else {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({ event: 'Geo Location', user_country: location?.name });
  }

  if (typeof $intercom !== 'undefined') {
    $intercom.boot({
      app_id: $config.intercomId,
    });
  }
});

const triggerSignUpEvent = () => {
  $mzAnalyticsApi({
    event: 'click',
    name: 'signup',
    productArea: 'profile',
  });
};
const upgradeButtonClickEvent = () => {
  $mzAnalyticsApi({
    event: 'click',
    name: 'upgrade',
    productArea: 'profile',
  });
};

const checkSubscriptionStatus = () => {
  const subscriptionStatus = user.value?.subscription?.status;
  // Show message to user if account expiring or payment failed
  if (subscriptionStatus === 'past_due' || subscriptionStatus === 'canceled') {
    // Payment failed
    if (subscriptionStatus === 'past_due') {
      store.dispatch('setHeaderBanner', {
        content:
          'Unfortunately your last payment failed so your account access is limited. Please update your billing details.',
        icon: 'exclamation-triangle',
        link: '/member/billing/edit-billing/',
      });
    }
    let date = user.value?.subscription?.expiry_date;
    // Canceled and less than 7 days to expiry
    if (!date) {
      return;
    }
    if (subscriptionStatus === 'canceled' && dayjs(date).diff(new Date(), 'day') < 7) {
      // Convert to UTC in JS format
      if (typeof date !== 'number' && date.charAt(date.length - 1) !== 'Z') {
        date = date.replace(' ', 'T') + '+00:00';
      }

      const time = dayjs(date).fromNow();
      store.dispatch('setHeaderBanner', {
        content:
          'Reminder: your subscription will expire in ' +
          time +
          ' days. Reactivate to continue using Teach Starter.',
        icon: 'exclamation-triangle',
        link: '/member/subscription/',
      });
    }
  }
};

const toggleMobileNavMethod = (show: boolean) => {
  store.dispatch('toggleMobileNav', show);
};

// signup/login events
const toggleLoginModal = (show: boolean) => store.dispatch('toggleLoginModal', show);
const toggleSignUpModal = () => {
  triggerSignUpEvent();
  store.dispatch('modals/show', 'signUpCombined');
};
const toggleLocationSelectorModal = (show: Boolean) => {
  store.dispatch('toggleLocationSelectorModal', show);
};

return { __sfc: true,HeaderBanner, Toast, MobileNav, $config, i18n, $isFlagOn, $addProfitwellScript, $cookies, $intercom, localePath, $getExperimentVariant, $mzAnalyticsApi, $route, store, user, doesUserGetPlusDownloads, plusDownloadsLeft, openNavEvent, linkClickedEvent, isFreeUser, isNonPlusMember, isPlusLiteMember, isSchoolMember, showHeaderBanner, trendingTopics, showMobileNav, locale, location, u, contentDropdown, secondRowDropdowns, isResourceDropdown, triggerSignUpEvent, upgradeButtonClickEvent, checkSubscriptionStatus, toggleMobileNavMethod, toggleLoginModal, toggleSignUpModal, toggleLocationSelectorModal, MegaMenu, SmallMegaMenu, TsTesLogo, TLinkButton, navigationLinks, ProfileDropdown, DownloadsPill, SearchBar, AlgoliaSearchBar, DropdownMenu, TNavigationIconItem, TButton }
}

})